import { Component, OnInit } from "@angular/core";
import { IContentHeader } from "../../../../layout/components/content-header/content-header.component";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { IBank } from "../../../../auth/models/bank";
import { CitiesService } from "../../../../auth/service/cities.service";
import { ListService } from "../../../../auth/service/list.service";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { AuthenticationService } from "../../../../auth/service";
import { UtilsService } from "../../../../auth/service/utils.service";
import { ProvidersService } from "../../providers.service";
import { IProvider, IProviderAddress, IProviderReferent } from "../../models/providers";
import nationsWithCodes from "app/auth/models/nations-with-codes";
import { IInvoiceType } from "../../../../auth/models/invoice-type";
import { environment } from "../../../../../environments/environment";

@Component({
  selector: "app-providers-edit",
  templateUrl: "./providers-edit.component.html",
  styleUrls: ["./providers-edit.component.scss"],
})
export class ProvidersEditComponent implements OnInit {
  public contentHeader: IContentHeader = null;
  public masterDataForm: FormGroup;
  public siteForm: FormGroup;
  public referentForm: FormGroup;
  public showErrorsMasterData: boolean = false;
  public showErrorsSite: boolean = false;
  public showErrorsReferents: boolean = false;
  public banksList: IBank[] = [];
  public invoiceTypeList: IInvoiceType[] = [];
  public nationsWithCodes: { name: string; code: string }[] = nationsWithCodes;
  public fileName: string = null;
  public logoBase64: string = "";
  private providerId: number = null;
  public provider: IProvider = null;
  public activeTable: string = "masterdata";
  public isEditModeReferent: boolean = false;
  public editingReferent: number = 0;
  public isEditModeSite: boolean = false;
  public editingAddress: number = 0;

  constructor(
    public _formBuilder: FormBuilder,
    public _citiesService: CitiesService,
    private _listService: ListService,
    private _authenticationService: AuthenticationService,
    public _utilService: UtilsService,
    private _providerService: ProvidersService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {
    this._utilService.disableAutoHideLoading();
    this.contentHeader = {
      headerTitle: "MENU.EDIT_PROVIDER",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/",
          },
          {
            name: "MENU.EXTERNAL_USERS_LIST",
            isLink: true,
            link: "/dashboard/providers/list",
          },
          {
            name: "MENU.EDIT_PROVIDER",
            isLink: false,
          },
        ],
      },
    };
    this.activatedRoute.paramMap.subscribe(async (params: ParamMap) => {
      const providerId: any = params.get("id");

      if (providerId) {
        if (!this._authenticationService.hasCapability("edit_provider")) {
          await this.router.navigateByUrl("/dashboard/main");
          return false;
        }
        this._utilService.disableAutoHideLoading();
        this.providerId = Number(providerId);
      }
    });
    this.masterDataForm = this._formBuilder.group({
      name: [null, [Validators.required, Validators.minLength(2), Validators.maxLength(255)]],
      business_name: [null, [Validators.required, Validators.minLength(2), Validators.maxLength(255)]],
      vat_number: [null, [Validators.minLength(9), Validators.maxLength(12)]],
      pec: [null, [Validators.maxLength(255), Validators.pattern("^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$")]],
      recipient_pec: [null, [Validators.maxLength(255), Validators.pattern("^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$")]],
      email: [null, [Validators.maxLength(255), Validators.pattern("^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$")]],
      fiscalcode: [null, [Validators.maxLength(255)]],
      invoice_type: [null, [Validators.required]],
      bank: [null, [Validators.required]],
      unique_code: [null],
    });

    this.siteForm = this._formBuilder.group({
      label: [null, [Validators.required, Validators.maxLength(255)]],
      street: [null, [Validators.required, Validators.maxLength(255)]],
      city_id: [null],
      province_id: [null],
      zipcode: [null, [Validators.minLength(5), Validators.maxLength(5)]],
      external: new FormControl(false),
      country: new FormControl(null),
    });

    this.referentForm = this._formBuilder.group({
      firstname: [null, [Validators.required, Validators.minLength(2), Validators.maxLength(255)]],
      lastname: [null, [Validators.required, Validators.minLength(2), Validators.maxLength(255)]],
      email: [null, [Validators.required, Validators.minLength(2), Validators.pattern("^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$")]],
      phone: [null],
    });
  }

  async ngOnInit() {
    if (!this._authenticationService.hasCapability("edit_provider")) {
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }
    await this.loadProvider();
    this.banksList = await this._listService.getList("bank");
    this.invoiceTypeList = await this._listService.getList("invoice_type");
    this._utilService.hideLoading();
  }

  async loadProvider() {
    await this._providerService.get(this.providerId).then((provider: IProvider) => {
      if (!!provider) {
        this.provider = provider;
        if (this.provider.logo) {
          this.provider.logo = environment.CDN_URL + "/" + this.provider.logo;
        }
        this.masterDataForm.patchValue({
          name: provider.name,
          business_name: provider.business_name,
          vat_number: provider.vat_number,
          pec: provider.pec,
          recipient_pec: provider.recipient_pec,
          email: provider.email,
          fiscalcode: provider.fiscalcode,
          invoice_type: provider.invoice_type,
          bank: provider.bank,
          unique_code: provider.unique_code,
        });
      }
    });
  }

  enterEditModeSite(address_id: number) {
    this.isEditModeSite = true;
    this.editingAddress = address_id;
    const address: IProviderAddress = this.provider.addresses.find((element: IProviderAddress) => element.id === address_id);
    this.siteForm.patchValue({
      label: address.label,
      street: address.street,
      city_id: address.city_id,
      province_id: address.province_id,
      zipcode: address.zipcode,
      external: address.external,
      country: address.country,
    });
  }

  enterEditModeReferent(referent_id: number) {
    this.isEditModeReferent = true;
    this.editingReferent = referent_id;
    const referent: IProviderReferent = this.provider.referents.find((element: IProviderReferent) => element.id === referent_id);
    this.referentForm.patchValue({
      firstname: referent.firstname,
      lastname: referent.lastname,
      phone: referent.phone,
      email: referent.email,
    });
  }

  resetEditModeSite() {
    this.isEditModeSite = false;
    this.editingAddress = 0;
    this.siteForm.reset();
  }

  resetEditModeReferent() {
    this.isEditModeReferent = false;
    this.editingReferent = 0;
    this.referentForm.reset();
  }

  masterDataHasError(field_name: string): boolean {
    if (this.showErrorsMasterData) {
      return this.masterDataForm.controls[field_name].status === "INVALID";
    } else {
      return this.masterDataForm.controls[field_name].status === "INVALID" && this.masterDataForm.controls[field_name].touched;
    }
  }

  siteHasError(field_name: string): boolean {
    if (this.showErrorsSite) {
      return this.siteForm.controls[field_name].status === "INVALID";
    } else {
      return this.siteForm.controls[field_name].status === "INVALID" && this.siteForm.controls[field_name].touched;
    }
  }

  referentHasError(field_name: string): boolean {
    if (this.showErrorsReferents) {
      return this.referentForm.controls[field_name].status === "INVALID";
    } else {
      return this.referentForm.controls[field_name].status === "INVALID" && this.referentForm.controls[field_name].touched;
    }
  }

  setAutoProvince() {
    const cityValue: number = this.siteForm.get("city_id").value;
    if (cityValue > 0) {
      const cityObj: any = this._citiesService.list.find((city: any) => city.id === cityValue);
      const province = this._citiesService.list.find((city: any) => city.initials === cityObj.initials && city.is_main === 1);
      this.siteForm.patchValue({ province_id: province.id });
    }
  }

  /*In caso di sede estera città zipcode e provincia saranno impostati e inviati a null*/
  manageExternalSite() {
    if (this.siteForm.value.external) {
      this.siteForm.patchValue({
        zipcode: null,
        city_id: null,
        province_id: null,
        country: null,
      });
      this.siteForm.get("country").addValidators(Validators.required);
    } else {
      this.siteForm.patchValue({
        zipcode: null,
        city_id: null,
        province_id: null,
        country: null,
      });
      this.siteForm.get("country").clearValidators();
    }
  }

  async handlerChangeFileInput(event: any) {
    if (!!event.target.value) {
      this.fileName = event.target.files[0].name;
      this._utilService.convertFileToBase64(event, (e: any) => {
        this.logoBase64 = e.target.result;
      });
    } else {
      this.logoBase64 = "";
      this.fileName = null;
    }
  }

  async addAddress() {
    this.showErrorsSite = false;
    if (this.siteForm.invalid) {
      await this._utilService.showSWALError({ text: "EMPTY_FIELD_FORM" });
      this.showErrorsSite = true;
      return false;
    }

    this._utilService.showLoading();

    let data: IProviderAddress = {
      ...this.siteForm.value,
      provider_id: this.providerId,
    };

    if (data.external) {
      data.city_id = null;
      data.province_id = null;
      data.zipcode = null;
    }

    return this._providerService
      .addAddress(data)
      .then(async () => {
        await this.loadProvider();
        this.siteForm.reset();
        this._utilService.hideLoading();
        this._utilService.showToastrSuccess("SUCCESS_ADD_ADDRESS");
      })
      .catch((err: any) => {
        this._utilService.hideLoading();
        this._utilService.showToastrError("ERROR_ADD_ADDRESS");
        console.error(err);
      });
  }

  async editAddress() {
    this.showErrorsSite = false;
    if (this.siteForm.invalid) {
      await this._utilService.showSWALError({ text: "ERROR_IN_FORM_SITE" });
      this.showErrorsSite = true;
      return false;
    }

    this._utilService.showLoading();
    let data: IProviderAddress = this.siteForm.value;

    if (data.external) {
      data.city_id = null;
      data.province_id = null;
      data.zipcode = null;
    }

    return this._providerService
      .editAddress(this.editingAddress, data)
      .then(async () => {
        await this.loadProvider();
        this.resetEditModeSite();
        this._utilService.hideLoading();
        this._utilService.showToastrSuccess("SUCCESS_EDIT_ADDRESS");
      })
      .catch((err: any) => {
        this._utilService.hideLoading();
        this._utilService.showToastrError("ERROR_EDIT_ADDRESS");
        console.error(err);
      });
  }

  async deleteAddress(address_id: number) {
    return this._utilService.showSWALDeleteConfirmation({ text: "ALERT.CONFIRM_DELETE_ADDRESS" }).then(async (result: any) => {
      if (result.value) {
        this._utilService.showLoading();
        await this._providerService
          .deleteAddress(address_id)
          .then(async () => {
            await this.loadProvider();
            this._utilService.hideLoading();
            this._utilService.showToastrSuccess("SUCCESS_DELETE_ADDRESS");
          })
          .catch((err: any) => {
            this._utilService.hideLoading();
            this._utilService.showToastrError("ERROR_DELETE_ADDRESS");
            console.error(err);
          });
      } else {
        await this._utilService.showSWALError({
          title: "ABORTED_TITLE",
          text: "ABORTED_TEXT",
        });
      }
    });
  }

  async addReferent() {
    this.showErrorsReferents = false;
    if (this.referentForm.invalid) {
      await this._utilService.showSWALError({ text: "EMPTY_FIELD_FORM" });
      this.showErrorsReferents = true;
      return false;
    }
    const data = { ...this.referentForm.value, provider_id: this.providerId };

    this._utilService.showLoading();
    await this._providerService
      .addReferent(data)
      .then(async () => {
        await this.loadProvider();
        this.referentForm.reset();
        this._utilService.hideLoading();
        await this._utilService.showToastrSuccess("SUCCESS_CREATE_REFERENT");
      })
      .catch((err: any) => {
        this._utilService.showToastrError("ERROR_CREATE_REFERENT");
        console.error(err);
      });
  }

  async editReferent() {
    this.showErrorsReferents = false;

    if (this.referentForm.invalid) {
      await this._utilService.showSWALError({ text: "ERROR_IN_FORM_REFERENT" });
      this.showErrorsReferents = true;
      return false;
    }

    const data: any = this.referentForm.value;

    this._utilService.showLoading();

    return this._providerService
      .editReferent(this.editingReferent, data)
      .then(async () => {
        await this.loadProvider();
        this.resetEditModeReferent();
        await this._utilService.hideLoading();
        this._utilService.showToastrSuccess("SUCCESS_EDIT_REFERENT");
      })
      .catch(async (err: any) => {
        await this._utilService.hideLoading();
        await this._utilService.showToastrError("ERROR_EDIT_REFERENT");
        console.error(err);
      });
  }

  async deleteReferent(id: number) {
    return this._utilService.showSWALDeleteConfirmation({ text: "ALERT.CONFIRM_DELETE_REFERENT" }).then(async (result: any) => {
      if (result.value) {
        await this._utilService.showLoading();
        await this._providerService
          .deleteReferent(id)
          .then(async () => {
            await this.loadProvider();
            await this._utilService.hideLoading();
            await this._utilService.showToastrSuccess("SUCCESS_DELETE_REFERENT");
          })
          .catch((err: any) => {
            this._utilService.showToastrError("ERROR_DELETE_REFERENT");
            console.error(err);
          });
      } else {
        await this._utilService.showSWALError({ text: "ABORTED_TEXT", title: "ABORTED_TITLE" });
      }
    });
  }

  async save() {
    if (!this._authenticationService.hasCapability("edit_provider")) {
      await this.router.navigateByUrl("/dashboard/main");
      return false;
    }
    this.showErrorsMasterData = false;
    if (this.masterDataForm.invalid) {
      this.showErrorsMasterData = true;
      return false;
    }
    if (!this.masterDataForm.value.fiscalcode && !this.masterDataForm.value.vat_number) {
      await this._utilService.showSWALError({ text: "INSERT_FISCALCODE_OR_VAT_NUMBER" });
      return false;
    }
    //Controllo che tutti i campi di sedi siano riempiti
    if (this.siteForm.value.city_id || this.siteForm.value.province_id || this.siteForm.value.zipcode || this.siteForm.value.street || this.siteForm.value.external) {
      if (!this.siteForm.value.external) {
        if (!this.siteForm.value.city_id || !this.siteForm.value.province_id || !this.siteForm.value.zipcode || !this.siteForm.value.street || this.siteForm.invalid) {
          this.showErrorsMasterData = true;
          await this._utilService.showSWALError({ text: "ERROR_FIEDLS_EMPTY_OFFICE_SITE" });
          return false;
        }
      } else {
        //Se sede estera controllo solo la strada
        if (!this.siteForm.value.street || !this.siteForm.value.country) {
          this.showErrorsMasterData = true;
          await this._utilService.showSWALError({ text: "ERROR_FIEDLS_EMPTY_OFFICE_SITE" });
          return false;
        }
      }
    }

    //Controllo che tutti i campi di sedi siano riempiti
    if (this.referentForm.value.firstname || this.referentForm.value.lastname || this.referentForm.value.email || this.referentForm.value.phone) {
      if (!this.referentForm.value.firstname || !this.referentForm.value.lastname || !this.referentForm.value.email || !this.referentForm.value.phone || this.referentForm.invalid) {
        this.showErrorsMasterData = true;
        await this._utilService.showSWALError({ text: "ERROR_FIEDLS_EMPTY_REFERENT" });
        return false;
      }
    }
    let data: IProvider = {
      ...this.masterDataForm.value,
      address: this.siteForm.value.street ? this.siteForm.value : null,
      referent: this.referentForm.value.firstname ? this.referentForm.value : null,
    };
    this._utilService.showLoading();
    await this._providerService
      .edit(this.providerId, data)
      .then(async () => {
        this._utilService.hideLoading();
        if (this.logoBase64 && this.logoBase64.length > 0) {
          const fileExtension: string = this.logoBase64.substring("data:image/".length, this.logoBase64.indexOf(";base64"));
          const fileName: string = `logo-${this._utilService.slugify_me(data.name)}.${fileExtension}`;
          const uploadFile = await this._utilService.upload_media("public", fileName, this.logoBase64);
          if (uploadFile) {
            await this._providerService.edit(this.providerId, { logo: "public/" + fileName }).catch(async () => this._utilService.showSWALError({ text: "ERROR_CREATE_PROVIDER" }));
          }
        }
        await this._utilService.showSWALSuccess({ text: "SUCCESS_EDIT_PROVIDER" });
        await this.router.navigateByUrl("/dashboard/providers/list");
      })
      .catch((err: any) => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_EDIT_PROVIDER" });
        console.error(err);
      });
  }
}
